"use client";
import { BlockBase } from "@/cms/blocks/registry/types";
import { ClientRenderer } from "@/cms/render/Renderer";
import { usePlacementContent } from "@/cms/render/usePlacementContent";
import React, { useMemo } from "react";
import { renderContentTeaser } from "./contentTeasers/registry";
import LangButton from "@/components/ui/LangButton";
import { useLocaleState } from "@/hooks/useLocaleState";
import type { AppLocale } from "@/i18n/config";
import { useSession } from "next-auth/react";
import { PERMISSIONS } from "@/core/permissions";
import { EditSVG } from "@/components/ui/SVG";
import { AnimatePresence } from "framer-motion";
import { ContentEditorOverlay } from "@/cms/editor/ContentEditor.overlay";
import dynamic from "next/dynamic";
import { Replace } from "lucide-react";
import { articlePageUrl } from "@/analytics/content-urls";

const HomePlacementItemsOverlay = dynamic(
  () =>
    import("@/app/admin/cms/placement/PlacementAdminPageClient").then(
      (m) => m.PlacementItemsOverlay,
    ),
  { ssr: false },
);

type HomePageProps = {
  initialLocale: AppLocale;
};

function HomePage({ initialLocale }: HomePageProps) {
  const { changeLocale, currentLang, setCurrentLang } =
    useLocaleState(initialLocale);
  const { data: session } = useSession();
  const [activeContentId, setActiveContentId] = React.useState<string | null>(
    null,
  );
  const [placementEditorOpen, setPlacementEditorOpen] = React.useState(false);
  const { data: placementData, isLoading: placementLoading } =
    usePlacementContent({
      slot: "home.page",
      locale: currentLang,
    });

  const userPermissions = ((session?.user as any)?.permissions ?? []) as string[];
  const isAdmin = (session?.user as any)?.role === "ADMIN";
  const canEditContent =
    Boolean(session?.user) &&
    (isAdmin ||
      userPermissions.includes(PERMISSIONS.CONTENT.EDIT) ||
      userPermissions.includes(PERMISSIONS.CONTENT.PUBLISH));

  const placementContents = useMemo(() => {
    if (!placementData?.items?.length) return [];
    return placementData.items
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => {
        const contentTitle =
          currentLang === "fa"
            ? (item.content?.title_fa ?? item.content?.title_en)
            : (item.content?.title_en ?? item.content?.title_fa);
        return {
          contentId: item.contentId ?? null,
          pageUrl: articlePageUrl(item.content, currentLang),
          blocks: item.blocks as BlockBase[],
          type: item.content?.type ?? "",
          viewMode: item.viewMode as
            | "list"
            | "card"
            | "carousel"
            | "previewCard"
            | undefined,
          title: contentTitle ?? null,
        };
      });
  }, [placementData, currentLang]);

  return (
    <div className="bg-grayCustom lg:bg-grayCustom relative min-h-screen px-4 lg:px-8">
      <h2 className="lg:hidden absolute top-3 left-5.5 z-30 text-lg">
        Where Cabling <br />
        <span className="text-xl font-bold">
          Meets Excellence
          <span className="ml-1 inline-block -translate-y-2 text-sm font-bold">
            ®
          </span>
        </span>
      </h2>
      <div className="bg-grayCustom/90 sticky lg:hidden top-16 z-20 flex justify-end py-2 pt-4 backdrop-blur-sm lg:top-5.5 lg:right-10 lg:z-50 lg:pt-0 lg:bg-white/90">
        <div className="flex items-center gap-3">
          {canEditContent ? (
            <button
              type="button"
              onClick={() => setPlacementEditorOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded-md p-1"
              aria-label="Edit home page placement"
              title="Edit home page placement"
            >
              <Replace className="text-pinkCustom"/>
            </button>
          ) : null}
          <LangButton
            currentLang={currentLang}
            setCurrentLang={setCurrentLang}
            onLocaleChange={changeLocale}
            bgColor="white"
          />
        </div>
      </div>
      <div className="hidden lg:block fixed top-6 right-1/2 z-40">
          {canEditContent ? (
            <button
              type="button"
              onClick={() => setPlacementEditorOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md p-1 cursor-pointer"
              aria-label="Edit home page placement"
              title="Edit home page placement"
            >
              <Replace className="text-pinkCustom"/>
            </button>
          ) : null}
      </div>
      {placementLoading && !placementContents.length ? (
        <HomePageSkeleton />
      ) : null}

      {placementContents.length > 0 && (
        <div className="flex flex-col space-y-6 py-10 lg:gap-10 lg:py-8">
          {placementContents.map((content, index) => {
            const teaser = renderContentTeaser({
              type: content.type,
              blocks: content.blocks,
              locale: currentLang,
              viewMode: content.viewMode,
              title: content.title,
              contentId: content.contentId,
              pageUrl: content.pageUrl,
              showEditButton: canEditContent,
              onRequestEdit: (contentId) => setActiveContentId(contentId),
            });
            return (
              <TeaserCard key={index}>
                {teaser ?? (
                  <div>
                    {canEditContent && content.title && content.contentId ? (
                      <div className="mb-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveContentId(content.contentId!)}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white"
                          aria-label="Edit content"
                          title="Edit content"
                        >
                          <EditSVG
                            className="h-5 w-5"
                            svgColor="pinkCustom"
                            accentColor="var(--color-gray-500)"
                          />
                        </button>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {content.title}
                        </h2>
                      </div>
                    ) : null}
                    <ClientRenderer blocks={content.blocks} locale={currentLang} />
                  </div>
                )}
              </TeaserCard>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {activeContentId ? (
          <ContentEditorOverlay
            contentId={activeContentId}
            onClose={() => setActiveContentId(null)}
            defaultLocale={currentLang}
          />
        ) : null}
        {placementEditorOpen && canEditContent ? (
          <HomePlacementItemsOverlay
            slot="home.page"
            categoryId={null}
            title="Home Page"
            actorEmail={session?.user?.email ?? null}
            onClose={() => setPlacementEditorOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default HomePage;

function TeaserCard({ children }: { children: React.ReactNode }) {
  return <div className="mb-10 lg:mb-0">{children}</div>;
}

function HomePageSkeleton() {
  return (
    <div className="py-6 lg:py-8">
      <div className="space-y-6 lg:hidden">
        <div className="space-y-3 rounded-2xl bg-white p-4">
          <SkeletonBlock className="h-44 w-full rounded-xl" />
          <SkeletonBlock className="h-5 w-2/3" />
          <SkeletonBlock className="h-4 w-full" />
          <SkeletonBlock className="h-4 w-5/6" />
        </div>
        <div className="space-y-3 rounded-2xl bg-white p-4">
          <SkeletonBlock className="h-5 w-1/2" />
          <SkeletonBlock className="h-36 w-full rounded-xl" />
          <SkeletonBlock className="h-4 w-5/6" />
          <SkeletonBlock className="h-4 w-2/3" />
        </div>
        <div className="space-y-3 rounded-2xl bg-white p-4">
          <SkeletonBlock className="h-5 w-2/5" />
          <SkeletonBlock className="h-36 w-full rounded-xl" />
          <SkeletonBlock className="h-4 w-3/4" />
          <SkeletonBlock className="h-4 w-1/2" />
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="space-y-10 pt-16 ">
          <div className="grid grid-cols-3 gap-x-6">
            <div className="space-y-4 rounded-2xl bg-white p-6">
              <SkeletonBlock className="h-8 w-1/3" />
              <SkeletonBlock className="h-72 w-full rounded-xl" />
            </div>
            <div className="space-y-4 rounded-2xl bg-white p-6">
              <SkeletonBlock className="h-8 w-1/3" />
              <SkeletonBlock className="h-72 w-full rounded-xl" />
            </div>
            <div className="space-y-4 rounded-2xl bg-white p-6">
              <SkeletonBlock className="h-8 w-1/3" />
              <SkeletonBlock className="h-72 w-full rounded-xl" />
            </div>

          </div>
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4 rounded-2xl bg-white p-6">
              <SkeletonBlock className="h-6 w-2/5" />
              <SkeletonBlock className="h-56 w-full rounded-xl" />
              <SkeletonBlock className="h-4 w-4/5" />
              <SkeletonBlock className="h-4 w-3/5" />
            </div>
            <div className="space-y-4 rounded-2xl bg-white p-6">
              <SkeletonBlock className="h-6 w-1/2" />
              <SkeletonBlock className="h-56 w-full rounded-xl" />
              <SkeletonBlock className="h-4 w-3/4" />
              <SkeletonBlock className="h-4 w-1/2" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonBlock({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-lg bg-gray-200 ${className}`} />;
}
